/**
 * Planverse data layer.
 *
 * Currently returns in-memory mock data. When Workflow ships, swap the two
 * exported functions (`listPlans`, `getPlan`) for calls to the real endpoint
 * (likely `/sdk/planbase/planverse` on the worker). The rest of the app does
 * not need to change.
 */

export interface PlanverseAuthor {
  id: string;
  name: string;
  username: string;
  initial: string;
}

export type PlanverseVisibility = 'public' | 'unlisted' | 'private';

export interface PlanversePlan {
  id: string;
  title: string;
  coverUrl: string;
  publishedAt: string; // ISO timestamp
  author: PlanverseAuthor;
  likes: number;
  duplicates: number;
  views: number;
  /** Free-text tags used for search; category label used for filter chips later. */
  tags: string[];
  category: string;
  /**
   * public   — appears on the /planverse feed
   * unlisted — only accessible via a shared link (not on the feed)
   * private  — owner-only (currently treated same as unlisted for viewing)
   */
  visibility: PlanverseVisibility;
}

const MOCK: PlanversePlan[] = [
  {
    id: 'p-3br-flat',
    title: '3 Bedroom flat',
    coverUrl:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&auto=format',
    publishedAt: hoursAgo(3),
    author: author('Tobi Ade', 'tobi', 'T'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['residential', 'floor-plan', 'compact'],
    category: 'residential', visibility: 'public',
  },
  {
    id: 'p-trafford-stadium',
    title: 'Trafford stadium',
    coverUrl:
      'https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=800&auto=format',
    publishedAt: hoursAgo(3),
    author: author('Jane Cooper', 'jane', 'J'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['sports', 'commercial', 'timber'],
    category: 'recreational', visibility: 'public',
  },
  {
    id: 'p-new-project',
    title: 'New Project',
    coverUrl:
      'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&auto=format',
    publishedAt: hoursAgo(3),
    author: author('Kenji Ito', 'kenji', 'K'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['masterplan', 'site-plan'],
    category: 'institutional', visibility: 'public',
  },
  {
    id: 'p-untitled',
    title: 'Untitled',
    coverUrl:
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format',
    publishedAt: hoursAgo(3),
    author: author('Alex Rivera', 'alex', 'A'),
    likes: 0,
    duplicates: 8,
    views: 8,
    tags: ['circular', 'plaza'],
    category: 'institutional', visibility: 'public',
  },
  {
    id: 'p-modern-office',
    title: 'Modern Office',
    coverUrl:
      'https://images.unsplash.com/photo-1524230572899-a752b3835840?w=800&auto=format',
    publishedAt: hoursAgo(2),
    author: author('Priya Menon', 'priya', 'P'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['office', 'commercial'],
    category: 'commercial', visibility: 'public',
  },
  {
    id: 'p-city-park',
    title: 'City Park R.',
    coverUrl:
      'https://images.unsplash.com/photo-1577223625816-7546f13df25d?w=800&auto=format',
    publishedAt: hoursAgo(2),
    author: author('Sofia Marín', 'sofia', 'S'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['landscape', 'public'],
    category: 'recreational', visibility: 'public',
  },
  {
    id: 'p-commercial',
    title: 'Commercial building',
    coverUrl:
      'https://images.unsplash.com/photo-1487958449943-2429e8be8625?w=800&auto=format',
    publishedAt: hoursAgo(1),
    author: author('Marcus Vale', 'marcus', 'M'),
    likes: 5,
    duplicates: 8,
    views: 8,
    tags: ['commercial', 'retail'],
    category: 'commercial', visibility: 'public',
  },
  {
    id: 'p-community',
    title: 'Community centre',
    coverUrl:
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&auto=format',
    publishedAt: hoursAgo(1),
    author: author('Nia Okafor', 'nia', 'N'),
    likes: 0,
    duplicates: 8,
    views: 8,
    tags: ['community', 'public'],
    category: 'institutional', visibility: 'public',
  },
];

function author(name: string, username: string, initial: string): PlanverseAuthor {
  return { id: username, name, username, initial };
}

function hoursAgo(h: number): string {
  return new Date(Date.now() - h * 60 * 60 * 1000).toISOString();
}

export async function listPlans(query = ''): Promise<PlanversePlan[]> {
  // Feed only shows public plans.
  const q = query.trim().toLowerCase();
  const publicOnly = MOCK.filter((p) => p.visibility === 'public');
  if (!q) return publicOnly;
  return publicOnly.filter(
    (p) =>
      p.title.toLowerCase().includes(q) ||
      p.tags.some((t) => t.includes(q)) ||
      p.author.name.toLowerCase().includes(q),
  );
}

export async function getPlan(id: string): Promise<PlanversePlan | null> {
  // The preview page can open unlisted + private plans by direct id.
  return MOCK.find((p) => p.id === id) ?? null;
}

export interface PublishInput {
  title: string;
  coverUrl: string;
  authorName: string;
  authorUsername: string;
  visibility: PlanverseVisibility;
  tags?: string[];
  category?: string;
}

/**
 * Publish a workflow to Planverse. In this mock, it appends to the in-memory
 * MOCK array and returns the new plan (with a stable id + a link the caller
 * can share). When the worker ships a /sdk/planbase/planverse POST, swap this
 * for a real fetch and keep the return shape.
 */
export async function publishPlan(input: PublishInput): Promise<PlanversePlan> {
  const slug = input.title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'untitled';
  const plan: PlanversePlan = {
    id: `p-${slug}-${Date.now().toString(36)}`,
    title: input.title || 'Untitled',
    coverUrl: input.coverUrl,
    publishedAt: new Date().toISOString(),
    author: author(input.authorName || 'You', input.authorUsername || 'you', (input.authorName?.[0] || 'Y').toUpperCase()),
    likes: 0,
    duplicates: 0,
    views: 0,
    tags: input.tags || [],
    category: input.category || 'other',
    visibility: input.visibility,
  };
  MOCK.unshift(plan);
  return plan;
}

/**
 * When Workflow ships, this becomes a real POST to `/sdk/planbase/planverse/:id/duplicate`
 * that clones the source canvas into a new draft in the caller's workspace and
 * returns the new draft id (which the caller navigates to under /orbi/workflow/:id).
 */
export async function duplicatePlan(_id: string): Promise<{ draftId: string } | null> {
  return null;
}
